// Pure measured-byte observations. No filesystem, provider, HTML writer,
// qualification, QA approval or delivery authority. Missing is not a clean scan.
export const ESP_RIVAL_LABELS = Object.freeze({ marketo: 'Marketo', hubspot: 'HubSpot', sfmc: 'SFMC', zeta: 'Zeta' });
const MAX_HTML = 900000, MAX_MATCHES = 20000, MAX_RUN = 4096, MAX_JSON = 2 * 1024 * 1024;
const encoder = new TextEncoder(), decoder = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true });
const SHA_SLUG = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const VERDICTS = new Set(['AGREE', 'CONTRADICT', 'MISSED-SLOT', 'NO-OPINION']);
const plain = v => !!v && Object.getPrototypeOf(v) === Object.prototype;
const closed = (v, keys) => plain(v) && Object.keys(v).length === keys.length && keys.every(k => Object.hasOwn(v, k));
const integer = (v, max) => Number.isSafeInteger(v) && v >= 0 && v <= max;
const validText = (v, max) => typeof v === 'string' && v.length > 0 && !v.includes('\0')
  && encoder.encode(v).length <= max && decoder.decode(encoder.encode(v)) === v;
const require = (condition, code = 'ESP_DIALECT_MEASUREMENT_INVALID') => {
  if (!condition) throw Object.assign(Error(code), { code });
};
const label = esp => Object.hasOwn(ESP_RIVAL_LABELS, esp) ? ESP_RIVAL_LABELS[esp] : null;
const codepointOrder = (a, b) => {
  const left = [...a], right = [...b];
  for (let i = 0; i < Math.min(left.length, right.length); i++) {
    const difference = left[i].codePointAt(0) - right[i].codePointAt(0);
    if (difference) return difference;
  }
  return left.length - right.length;
};
// Python's ensure_ascii=True includes DEL and escapes each UTF-16 surrogate.
const asciiJsonLength = value => JSON.stringify(value).replace(/[\u007f-\uffff]/g,
  char => '\\u' + char.charCodeAt(0).toString(16).padStart(4, '0')).length;

export function espMeasurementFieldsValid(verdict) {
  try {
    if (!verdict || typeof verdict !== 'object') return false;
    const hasPurity = Object.hasOwn(verdict, 'purity'), hasAccounting = Object.hasOwn(verdict, 'accounting');
    if (!hasPurity && !hasAccounting) return true;
    if (!hasPurity || !hasAccounting) return false;
    const { purity, accounting, seams } = verdict;
    if (purity === null && accounting === null) return true;
    if (!closed(purity, ['own', 'foreign']) || !integer(purity.own, MAX_MATCHES)
      || !Array.isArray(purity.foreign) || purity.foreign.length + purity.own > MAX_MATCHES
      || !closed(accounting, ['seams_in', 'tokens_out', 'escalated', 'missed'])
      || !integer(accounting.seams_in, 1000) || !integer(accounting.tokens_out, MAX_MATCHES)
      || !integer(accounting.escalated, 1000) || !integer(accounting.missed, 1000)
      || !Array.isArray(seams) || seams.length !== accounting.seams_in
      || seams.some(s => !s || !VERDICTS.has(s.verdict))
      || accounting.tokens_out !== purity.own + purity.foreign.length
      || accounting.escalated !== seams.filter(s => s.verdict === 'NO-OPINION').length
      || accounting.missed !== seams.filter(s => s.verdict === 'MISSED-SLOT').length) return false;
    let priorEnd = -1;
    for (const found of purity.foreign) {
      if (!closed(found, ['esp', 'label', 'token', 'source_offset']) || typeof found.esp !== 'string' || !SHA_SLUG.test(found.esp)
        || found.label !== label(found.esp) || !validText(found.token, 512) || !integer(found.source_offset, MAX_HTML)
        || found.source_offset < priorEnd || found.source_offset + found.token.length > MAX_HTML) return false;
      priorEnd = found.source_offset + found.token.length;
    }
    return asciiJsonLength({ purity, accounting }) <= MAX_JSON;
  } catch { return false; }
}

// These expressions identify reviewed counting implementations, NOT runtime
// qualification. Never execute caller-controlled regular expressions.
const OWN_PATTERNS = Object.freeze({
  sfmc: String.raw`%%\[[^\]]*\]%%|%%=[^=]+=%%|%%[A-Za-z_]+%%`,
  marketo: String.raw`class="mkto\w+"|<meta class="mkto|\$\{\w+\}|\{\{(?:system|lead|my)\.[^}]+\}\}`,
  hubspot: String.raw`\{%\s*(?:rich_text|linked_image|boolean|choice|for|if|set|text)\b|\{\{\s*unsubscribe_link|\{\{\s*site_settings\.|hs-inline-css`,
});
// A fictional, fixed literal counter for the offline two-pack qualification.
// Not a vendor profile, a regex evaluator, or a runtime admission grant.
const SYNTHETIC_PATTERN = String.raw`\[\[SYNTHETIC_(?:UNSUB|PREFERENCE|VIEW)_URL\]\]`;
const SYNTHETIC_TOKENS = ['[[SYNTHETIC_UNSUB_URL]]', '[[SYNTHETIC_PREFERENCE_URL]]', '[[SYNTHETIC_VIEW_URL]]'];
const asciiWord = char => {
  require(char.codePointAt(0) <= 127, 'ESP_DIALECT_MEASUREMENT_UNSUPPORTED');
  return char === '_' || char >= 'a' && char <= 'z' || char >= 'A' && char <= 'Z' || char >= '0' && char <= '9';
};
const pythonSpace = char => {
  const c = char.codePointAt(0);
  return c >= 0x09 && c <= 0x0d || c >= 0x1c && c <= 0x20 || c === 0x85 || c === 0xa0 || c === 0x1680
    || c >= 0x2000 && c <= 0x200a || c === 0x2028 || c === 0x2029 || c === 0x202f || c === 0x205f || c === 0x3000;
};
const pointAt = (source, offset) => String.fromCodePoint(source.codePointAt(offset));
function countOwn(pattern, source) {
  if (pattern === SYNTHETIC_PATTERN) {
    let cursor = 0, count = 0, candidates = 0;
    while (cursor < source.length) {
      if (source.startsWith('[[SYNTHETIC_', cursor)) require(++candidates <= MAX_MATCHES, 'ESP_DIALECT_MEASUREMENT_LIMIT');
      const literal = SYNTHETIC_TOKENS.find(token => source.startsWith(token, cursor));
      if (literal) { require(++count <= MAX_MATCHES, 'ESP_DIALECT_MEASUREMENT_LIMIT'); cursor += literal.length; }
      else cursor += pointAt(source, cursor).length;
    }
    return count;
  }
  const family = Object.keys(OWN_PATTERNS).find(key => OWN_PATTERNS[key] === pattern);
  require(family, 'ESP_DIALECT_MEASUREMENT_UNSUPPORTED');
  function run(start, predicate) {
    let end = start, length = 0;
    while (end < source.length) {
      const char = pointAt(source, end);
      if (!predicate(char)) break;
      end += char.length;
      require(++length <= MAX_RUN, 'ESP_DIALECT_MEASUREMENT_LIMIT');
    }
    return end;
  }
  let cursor = 0, count = 0, candidates = 0;
  while (cursor < source.length) {
    let end = null, candidate = false;
    if (family === 'sfmc' && source.startsWith('%%', cursor)) {
      candidate = true;
      if (source.startsWith('%%[', cursor)) {
        const stop = run(cursor + 3, char => char !== ']');
        if (source.startsWith(']%%', stop)) end = stop + 3;
      } else if (source.startsWith('%%=', cursor)) {
        const stop = run(cursor + 3, char => char !== '=');
        if (stop > cursor + 3 && source.startsWith('=%%', stop)) end = stop + 3;
      } else {
        const stop = run(cursor + 2, char => char === '_' || char >= 'A' && char <= 'Z' || char >= 'a' && char <= 'z');
        if (stop > cursor + 2 && source.startsWith('%%', stop)) end = stop + 2;
      }
    } else if (family === 'marketo') {
      if (source.startsWith('class="mkto', cursor)) {
        candidate = true;
        const start = cursor + 'class="mkto'.length, stop = run(start, asciiWord);
        if (stop > start && source.startsWith('"', stop)) end = stop + 1;
      } else if (source.startsWith('<meta class="mkto', cursor)) {
        candidate = true; end = cursor + '<meta class="mkto'.length;
      } else if (source.startsWith('${', cursor)) {
        candidate = true;
        const stop = run(cursor + 2, asciiWord);
        if (stop > cursor + 2 && source.startsWith('}', stop)) end = stop + 1;
      } else {
        const prefix = ['{{system.', '{{lead.', '{{my.'].find(value => source.startsWith(value, cursor));
        if (prefix) {
          candidate = true;
          const start = cursor + prefix.length, stop = run(start, char => char !== '}');
          if (stop > start && source.startsWith('}}', stop)) end = stop + 2;
        }
      }
    } else if (family === 'hubspot') {
      if (source.startsWith('{%', cursor)) {
        candidate = true;
        const start = run(cursor + 2, pythonSpace);
        for (const keyword of ['rich_text', 'linked_image', 'boolean', 'choice', 'for', 'if', 'set', 'text']) {
          const stop = start + keyword.length;
          if (source.startsWith(keyword, start) && (stop === source.length || !asciiWord(pointAt(source, stop)))) {
            end = stop; break;
          }
        }
      } else if (source.startsWith('{{', cursor)) {
        candidate = true;
        const start = run(cursor + 2, pythonSpace);
        for (const prefix of ['unsubscribe_link', 'site_settings.']) {
          if (source.startsWith(prefix, start)) { end = start + prefix.length; break; }
        }
      } else if (source.startsWith('hs-inline-css', cursor)) {
        candidate = true; end = cursor + 'hs-inline-css'.length;
      }
    }
    if (candidate) require(++candidates <= MAX_MATCHES, 'ESP_DIALECT_MEASUREMENT_LIMIT');
    if (end !== null) {
      require(++count <= MAX_MATCHES, 'ESP_DIALECT_MEASUREMENT_LIMIT');
      cursor = end;
    } else cursor += pointAt(source, cursor).length;
  }
  return count;
}

export function measureEspDialect(htmlBytesOrString, profile, seams) {
  let source, bytes;
  if (typeof htmlBytesOrString === 'string') {
    require(htmlBytesOrString.length <= MAX_HTML);
    bytes = encoder.encode(htmlBytesOrString); source = decoder.decode(bytes);
    require(source === htmlBytesOrString);
  } else {
    require(htmlBytesOrString instanceof Uint8Array);
    require(htmlBytesOrString.byteLength <= MAX_HTML);
    bytes = htmlBytesOrString;
    // Preserve the legacy exact-byte writer's invalid-UTF8 uncertainty. Fresh
    // derived scans reject those bytes upstream; never invent clean counters.
    try { source = decoder.decode(bytes); } catch { return { purity: null, accounting: null }; }
  }
  require(bytes.length <= MAX_HTML && !source.includes('\0') && plain(profile));
  if (profile.dialect_regex == null || profile.forbidden == null) return { purity: null, accounting: null };
  require(Array.isArray(seams) && seams.length <= 1000 && seams.every(s => s && VERDICTS.has(s.verdict)));
  const forbidden = profile.forbidden;
  require(plain(forbidden) && Object.keys(forbidden).length <= 64);
  let markerCount = 0;
  for (const [esp, markers] of Object.entries(forbidden)) {
    require(SHA_SLUG.test(esp) && Array.isArray(markers) && markers.length <= 128
      && markers.every(marker => validText(marker, 512)) && new Set(markers).size === markers.length);
    markerCount += markers.length;
  }
  require(markerCount <= 512);
  const own = countOwn(profile.dialect_regex, source), found = [];
  for (const esp of Object.keys(forbidden).sort(codepointOrder)) {
    for (const marker of [...forbidden[esp]].sort(codepointOrder)) {
      let cursor = 0;
      while (cursor <= source.length) {
        const start = source.indexOf(marker, cursor);
        if (start < 0) break;
        found.push({ start, end: start + marker.length, esp, token: marker, codepoints: [...marker].length });
        require(found.length + own <= MAX_MATCHES, 'ESP_DIALECT_MEASUREMENT_LIMIT');
        cursor = start + pointAt(source, start).length;
      }
    }
  }
  const order = (a, b) => a.start - b.start || a.end - b.end || codepointOrder(a.esp, b.esp) || codepointOrder(a.token, b.token);
  found.sort(order);
  const groups = [];
  for (const item of found) {
    const prior = groups.at(-1);
    if (prior && item.start < prior.end) { prior.end = Math.max(prior.end, item.end); prior.items.push(item); }
    else groups.push({ end: item.end, items: [item] });
  }
  const selected = groups.flatMap(group => {
    require(new Set(group.items.map(item => item.esp)).size === 1, 'ESP_FOREIGN_DIALECT_AMBIGUOUS');
    const accepted = [];
    for (const item of group.items.sort((a, b) => b.codepoints - a.codepoints || a.start - b.start || codepointOrder(a.token, b.token))) {
      let low = 0, high = accepted.length;
      while (low < high) { const mid = (low + high) >>> 1; if (accepted[mid].start < item.start) low = mid + 1; else high = mid; }
      if (low > 0 && accepted[low - 1].end > item.start || low < accepted.length && accepted[low].start < item.end) continue;
      accepted.splice(low, 0, item);
    }
    return accepted;
  }).sort(order);
  const fields = { purity: { own, foreign: selected.map(item => ({ esp: item.esp, label: label(item.esp),
    token: item.token, source_offset: item.start })) }, accounting: { seams_in: seams.length,
    tokens_out: own + selected.length, escalated: seams.filter(s => s.verdict === 'NO-OPINION').length,
    missed: seams.filter(s => s.verdict === 'MISSED-SLOT').length } };
  require(asciiJsonLength(fields) <= MAX_JSON, 'ESP_DIALECT_MEASUREMENT_LIMIT');
  require(espMeasurementFieldsValid({ ...fields, seams }));
  return fields;
}
